import axios from 'axios';
import { TOMAN_TO_RIAL_FACTOR } from '../constants';
import { USDRateData } from '../types/rateTypes';

// Mock data for development and fallback
const MOCK_USD_RATE: USDRateData = {
    usd_cash_rate: 100000,  // 93,000 Toman * 10 = 930,000 Rial
    last_updated: new Date().toISOString(),
    source: 'Mesghal (Mock)'
};

/**
 * Helper function to find <p> element by text content
 */
const findElementByText = (doc: Document, searchTerm: string): Element | null => {
    // Specifically look for <p> elements first
    const pElements = doc.getElementsByTagName('p');
    for (const element of pElements) {
        const text = element.textContent?.trim() || '';
        if (text.includes(searchTerm)) {
            return element;
        }
    }

    // Fallback: search all elements if no <p> found
    const elements = doc.getElementsByTagName('*');
    for (const element of elements) {
        const text = element.textContent?.trim() || '';
        if (text.includes(searchTerm)) {
            return element;
        }
    }
    return null;
};

/**
 * Helper function to extract number from element or its children/siblings
 */
const extractNumberFromElement = (element: Element | null): string => {
    if (!element) return '0';

    // If this is a <p> element, look for the next <h3> sibling specifically
    if (element.tagName === 'P') {
        let current = element.nextElementSibling;
        while (current) {
            if (current.tagName === 'H3') {
                const h3Text = current.textContent?.trim() || '';
                return h3Text;
            }
            current = current.nextElementSibling;
        }
    }

    // Check the element itself
    const elementText = element.textContent?.trim() || '';
    const numberMatch = elementText.match(/\d+/);
    if (numberMatch) {
        return numberMatch[0];
    }

    // Check children
    const children = element.children;
    for (let i = 0; i < children.length; i++) {
        const childText = children[i].textContent?.trim() || '';
        const childNumberMatch = childText.match(/\d+/);
        if (childNumberMatch) {
            return childNumberMatch[0];
        }
    }

    // Check next sibling (this is where the h3 with the rate is located)
    let current = element.nextElementSibling;
    while (current) {
        const text = current.textContent?.trim() || '';
        if (/\d/.test(text)) {
            return text;
        }
        current = current.nextElementSibling;
    }

    // Check parent's next sibling (for cases where the number is in a sibling div)
    if (element.parentElement) {
        let parentSibling = element.parentElement.nextElementSibling;
        while (parentSibling) {
            const text = parentSibling.textContent?.trim() || '';
            if (/\d/.test(text)) {
                return text;
            }
            parentSibling = parentSibling.nextElementSibling;
        }
    }

    return '0';
};

/**
 * Fetches USD cash rate from Mesghal.com
 * Only gets قیمت اسکناس دلار (USD Cash Rate)
 */
export const fetchUSDCashRate = async (): Promise<USDRateData> => {
    try {
        // Use localhost:3001 in development, /api in production
        const apiUrl = import.meta.env.DEV
            ? 'http://localhost:3001/api/mesghal'
            : '/api/mesghal';

        const response = await axios.get(apiUrl, {
            timeout: 5000,
            headers: {
                'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
                'Accept-Language': 'en-US,en;q=0.5',
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
            }
        });

        // Check if DOMParser is available (browser environment)
        if (typeof DOMParser === 'undefined') {
            console.warn('⚠️ DOMParser not available, using mock data');
            return MOCK_USD_RATE;
        }

        // Parse HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(response.data, 'text/html');

        // Extract only USD cash rate
        const usdCashElement = findElementByText(doc, 'قیمت اسکناس دلار');

        if (!usdCashElement) {
            console.error('❌ Could not find "قیمت اسکناس دلار" element');
            return MOCK_USD_RATE;
        }

        const rawRate = extractNumberFromElement(usdCashElement);

        // Clean the rate (remove non-numeric characters)
        const cleanRate = rawRate.replace(/[^\d]/g, '');

        // Convert Toman to Rial
        const usdCashRate = parseFloat(cleanRate) * TOMAN_TO_RIAL_FACTOR;

        // Validate the rate
        if (!usdCashRate || usdCashRate <= 0) {
            console.warn('⚠️ Invalid USD rate received, using mock data');
            return MOCK_USD_RATE;
        }

        const rateData: USDRateData = {
            usd_cash_rate: usdCashRate,
            last_updated: new Date().toISOString(),
            source: 'Mesghal'
        };

        return rateData;

    } catch (error) {
        console.error('❌ Error fetching from Mesghal:', error);
        throw error; // Re-throw the error instead of falling back to mock data
    }
};

/**
 * Main function to get USD cash rate from Mesghal
 */
export const getMesghalUSDCashRate = async (): Promise<USDRateData> => {
    return await fetchUSDCashRate();
}; 